import { Injectable } from '@angular/core';
import { CashJournalService, CashOpenType, CashOpenOrigin } from './cash-journal.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class HardwareService {

  private port: any;

  constructor(
    private cashJournalService: CashJournalService,
    private authService: AuthService
  ) {
    // We no longer auto-connect globally to avoid locking the port.
    // Each action will open, use, and close the port.
  }

  /**
   * Triggers the cash drawer via Web Serial API.
   * Connects to the FIRST available port and sends the open command.
   * Auto-closes the port to prevent locking.
   * @param origin Optional origin for logging. Default is BUTTON.
   * @param amount Optional amount to log (for F8 shortcuts). Default is 0.
   */
  async openDrawer(origin: CashOpenOrigin = CashOpenOrigin.BUTTON, amount: number = 0): Promise<boolean> {
    try {
      if (!window.isSecureContext) {
        console.warn('⚠️ Web Serial requires HTTPS or localhost');
        return false;
      }

      if (!('serial' in navigator)) {
        console.warn('❌ Web Serial API not supported');
        return false;
      }

      // 1. Get/Request Port
      // @ts-ignore
      const ports = await navigator.serial.getPorts();
      if (ports.length > 0) {
        this.port = ports[0];
      } else {
        // @ts-ignore
        this.port = await navigator.serial.requestPort();
      }

      if (!this.port) return false;

      // 2. Open Port
      await this.port.open({ baudRate: 9600 });

      // 3. Write Command
      const textEncoder = new TextEncoder();
      const writer = this.port.writable.getWriter();
      await writer.write(textEncoder.encode('open\n'));
      writer.releaseLock();

      // 4. Close Port Immediately to release the lock for AHK
      await this.port.close();
      this.port = null;

      // 5. Log event if it's a manual trigger (Shortcuts or Buttons)
      // Orders handle their own logging with specific amounts/order IDs.
      if (origin === CashOpenOrigin.BUTTON || origin === CashOpenOrigin.SHORTCUT) {
        this.logManualOpening(origin, amount);
      }

      return true;

    } catch (error) {
      console.error('Error in HardwareService.openDrawer:', error);
      // Ensure port is closed even on error
      if (this.port) {
        try { await this.port.close(); } catch (e) { }
        this.port = null;
      }
      return false;
    }
  }

  /**
   * Opens the physical cash drawer WITHOUT logging any F8 event.
   * Used when the caller manages F8 logging explicitly (e.g. F8-to-order linking).
   */
  async openDrawerWithoutLog(): Promise<boolean> {
    try {
      if (!window.isSecureContext) {
        console.warn('⚠️ Web Serial requires HTTPS or localhost');
        return false;
      }

      if (!('serial' in navigator)) {
        console.warn('❌ Web Serial API not supported');
        return false;
      }

      // @ts-ignore
      const ports = await navigator.serial.getPorts();
      if (ports.length > 0) {
        this.port = ports[0];
      } else {
        // @ts-ignore
        this.port = await navigator.serial.requestPort();
      }

      if (!this.port) return false;

      await this.port.open({ baudRate: 9600 });
      const writer = this.port.writable.getWriter();
      await writer.write(new TextEncoder().encode('open\n'));
      writer.releaseLock();
      await this.port.close();
      this.port = null;

      return true;
    } catch (error) {
      console.error('Error in HardwareService.openDrawerWithoutLog:', error);
      if (this.port) {
        try { await this.port.close(); } catch (e) { }
        this.port = null;
      }
      return false;
    }
  }

  private logManualOpening(origin: CashOpenOrigin, amount: number = 0) {
    const user = this.authService.getUser();
    const username = user ? user.username : 'UNKNOWN';

    const journal = {
      type: CashOpenType.WITHOUT_ORDER,
      origin: origin,
      username: username,
      reason: origin === CashOpenOrigin.SHORTCUT ? 'Ouverture via Raccourci F8 (Navigateur)' : 'Ouverture Manuelle (Bouton)',
      amount: amount
    };

    this.cashJournalService.logEvent(journal).subscribe({
      next: () => console.log(`✅ Cash drawer opening logged (Origin: ${origin}, Amount: ${amount})`),
      error: (err) => console.error('Failed to log cash open event', err)
    });
  }

  /**
   * Checks if a Cash Drawer (Serial Port) is available/authorized.
   */
  async isDrawerAvailable(): Promise<boolean> {
    if (!('serial' in navigator)) return false;
    // @ts-ignore
    const ports = await navigator.serial.getPorts();
    return ports.length > 0;
  }
}
